import { Button, Grid, GridItem, Heading, Loader, SelectInput, Text } from '@digitools/honest-ui';
import { DeleteIcon } from '@digitools/honest-ui/icons';
import React, { useRef, useState } from 'react';
import HonestSelectCategory from 'src/components/documentUpload/HonestSelectCategory';
import HonestUploadButton from 'src/components/documentUpload/HonestUploadButton';
import HonestUploadErrorAlerts from 'src/components/documentUpload/HonestUploadErrorAlerts';
import useDocumentUpload from 'src/components/documentUpload/useDocumentUpload';
import { TEALIUM_EVENT } from 'src/constants/tealiumConstants';
import useAuthentication from 'src/hooks/useAuthentication';
import useExperience from 'src/hooks/useExperience';
import useTealium from 'src/hooks/useTealium';
import useTranslate from 'src/hooks/useTranslate';
import useCliCustomerConfigContext from 'src/packages/cli/hooks/useCliContext';
import { docMeta } from 'src/packages/status/components/pages/ClaimDetails/docCategoryMapper';
import { Experience } from 'src/types/Experience';
import { Language } from 'src/types/Language';
import { CustomEmployeeText } from 'src/utils/CustomExperienceText';
import Sawmill from 'src/utils/Sawmill';
import { DocumentUploadDisplayTextEn } from './language/DocumentUploadDisplayTextEn';
import { DocumentUploadDisplayTextEs } from './language/DocumentUploadDisplayTextEs';
// @ts-ignore
import Styles from './HonestInlineDocumentUpload.scss';
/**
 * honest-ui rebrand of `./InlineDocumentUpload`, alongside the original until both consuming
 * pages are converted. Carries the original's behaviour as-is, category branch included.
 */
const HonestInlineDocumentUpload = ({ productName, documents, setDocuments, }) => {
    const { language, experience } = useExperience();
    // A deleted row unmounts the delete button holding focus; focus returns to the upload button.
    const uploadButtonRef = useRef(null);
    const { trackEvent } = useTealium();
    // A Tealium failure must not break the upload or delete flow.
    const safeTrackEvent = (params) => {
        try {
            trackEvent(params);
        }
        catch (error) {
            Sawmill.error(error instanceof Error ? error : new Error(String(error)), 'Tealium trackEvent failed');
        }
    };
    const { authHeader, user } = useAuthentication();
    const { t } = useTranslate();
    const languageFile = language === Language.SPANISH ? DocumentUploadDisplayTextEs : DocumentUploadDisplayTextEn;
    const [documentCategory, setDocumentCategory] = useState('');
    const { isClaimLeaveCustomer } = useCliCustomerConfigContext();
    // Claim vs Coordinated categories, for CLI.
    const isCoordinatedClaim = !!(productName === 'STD' && isClaimLeaveCustomer);
    // Product-specific analytics and copy, unrelated to categories.
    let tealiumEventType = '';
    let tealiumEventVersion = TEALIUM_EVENT.EVENT_VERSION.REVIEW;
    let documentUploadLabel = '';
    if (productName === 'LIFE') {
        tealiumEventType = TEALIUM_EVENT.EVENT_TYPE.LIFE_CLAIM_INTAKE;
        tealiumEventVersion = 'additional info';
        documentUploadLabel = `${languageFile.informationalMessage1Life}`;
    }
    else if (productName === 'AS') {
        tealiumEventType = TEALIUM_EVENT.EVENT_TYPE.ACCOMMODATION;
        tealiumEventVersion = 'Your accommodation details';
        documentUploadLabel = `${languageFile.informationalMessage1AS}`;
    }
    else {
        tealiumEventType = TEALIUM_EVENT.EVENT_TYPE.CLAIM_LEAVE_INTAKE;
        documentUploadLabel = CustomEmployeeText(experience === Experience.EMPLOYEE
            ? `${languageFile.informationalMessage1} ${languageFile.yourAbsenceEE}`
            : `${languageFile.informationalMessage1} ${languageFile.yourAbsenceER}`);
    }
    let categoryList = docMeta.s1.noCategory;
    if (productName === 'LEAVE') {
        categoryList = docMeta.s1.leaves;
    }
    else if (isCoordinatedClaim) {
        categoryList = experience === Experience.EMPLOYEE ? docMeta.s1.coordinatedEE : docMeta.s1.coordinatedER;
    }
    else if (productName === 'STD') {
        if (experience === Experience.EMPLOYEE) {
            categoryList = user?.customerInContext?.smallMid ? docMeta.docsNet.claimsEE : docMeta.s1.claimsEE;
        }
        else {
            categoryList = docMeta.s1.claimsER;
        }
    }
    const getDocMeta = (val) => {
        const metaObj = t(categoryList).filter((e) => e.label == val);
        return metaObj[0] || t(docMeta.s1.noCategory)[0];
    };
    const { errorMessage, apiFailureMessage, uploading, onUpload } = useDocumentUpload({
        languageFile,
        interviewType: productName.toUpperCase(),
        // Experience is set to Public for LCI for an unknown reason, so LIFE overrides to Employer.
        // TODO: update LCI component to make sure experience is properly set
        experience: productName === 'LIFE' ? 'Employer' : experience,
        authHeader: authHeader(),
        onUploadSuccess: (response, file) => {
            const meta = getDocMeta(documentCategory);
            setDocuments([
                ...documents,
                {
                    documentName: file.name,
                    docId: response.data,
                    label: meta.label,
                    documentTypeTitle: meta.title,
                    documentType: meta.type,
                },
            ]);
            setDocumentCategory('');
            safeTrackEvent({
                event_action: TEALIUM_EVENT.EVENT_ACTION.UPLOAD,
                event_type: tealiumEventType,
                event_name: 'open',
                event_version: tealiumEventVersion,
                event_results: '1',
            });
        },
        onValidationError: (type, message) => {
            // Empty-file rejections did not previously fire an analytics event.
            if (type === 'empty') {
                return;
            }
            safeTrackEvent({
                event_action: TEALIUM_EVENT.EVENT_ACTION.ERROR,
                event_type: tealiumEventType,
                event_name: message,
                event_version: 'upload document',
            });
        },
        onApiError: () => {
            setDocumentCategory('');
            safeTrackEvent({
                event_action: TEALIUM_EVENT.EVENT_ACTION.ERROR,
                event_type: tealiumEventType,
                event_name: languageFile.apiFailureMessage + languageFile.contactUs,
                event_version: 'upload document',
            });
        },
    });
    const updateCategory = (docDetails, val) => {
        const updatedCategory = documents.map(e => {
            const metaDoc = getDocMeta(val);
            if (e.docId == docDetails.docId) {
                return { ...e, label: metaDoc.label, documentTypeTitle: metaDoc.title, documentType: metaDoc.type };
            }
            return e;
        });
        setDocuments(updatedCategory);
    };
    const renderCategoryColumns = () => documents?.map((documentDetail, index) => (<li key={documentDetail.docId} className={Styles.categoryRow}>
        <Text as='span' className={Styles.categorySerial}>
          {index + 1}
        </Text>
        <SelectInput key={`${documentDetail.docId}-${documentDetail.label}`} label={languageFile.labelName} hideLabel={true} name={`doc-category-${documentDetail.docId}`} options={t(categoryList)} data-testid={'doc-category'} defaultValue={documentDetail?.label} onChange={value => updateCategory(documentDetail, value)}/>
        {getSelectedDocument(documentDetail)}
      </li>));
    const getSelectedDocument = (document) => {
        const handleDelete = () => {
            safeTrackEvent({
                event_action: TEALIUM_EVENT.EVENT_ACTION.BUTTON,
                event_type: TEALIUM_EVENT.EVENT_TYPE.DOCUMENT_UPLOAD,
                event_name: 'Delete Uploaded document',
            });
            setDocuments(documents.filter(file => file.docId !== document.docId));
            uploadButtonRef.current?.focus();
        };
        return (<li key={document.docId} className={Styles.documentRow} data-testid={'submitted-doc'} data-private={true}>
        <Text as='span' size='sm' weight='bold' className={Styles.documentName}>
          {document.documentName}
        </Text>
        <Button type={'text'} size={'small'} label={languageFile.delete} icon={<DeleteIcon />} iconPosition={'leading'} className={Styles.deleteButton} data-testid={'delete-doc'} 
        // Every row's visible label is just "Delete", so the filename makes the name unique.
        aria-label={`${languageFile.delete} ${document.documentName}`} onClick={handleDelete}/>
      </li>);
    };
    const showCategory = documents?.length < 10 && productName !== 'LIFE' && productName !== 'AS';
    return (<div data-testid='inline-document-upload'>
      <Loader id='document-upload-spinner' isShown={uploading} language={languageFile.language}>
        <Grid>
          <GridItem colSpan={4} colSpanMd={6} colSpanLg={12}>
            <HonestUploadErrorAlerts errorMessage={errorMessage} apiFailureMessage={apiFailureMessage} languageFile={languageFile}/>
          </GridItem>
          {/* One GridItem for both paragraphs: Figma wants --spacing-md between them, not the
            Grid's --spacing-xl row-gap. Bottom margin because legacy.scss zeroes margin-top
            on honest-ui elements. */}
          <GridItem colSpan={4} colSpanMd={6} colSpanLg={12}>
            <Text as='p' className='hui-mb-md' data-testid={'informationText'}>
              {documentUploadLabel}
            </Text>
            <Text as='p' data-testid={'acceptableDocumentsText'}>
              {languageFile.informationalMessage2}
            </Text>
          </GridItem>
          {showCategory ? (<GridItem colSpan={4} colSpanMd={6} colSpanLg={12}>
              {/* As in the original, the enclosing page supplies the form: honest-ui's
                SelectInput throws outside a <Form>, just as the informed one required an
                AccessibleForm ancestor. No consumer reaches this branch until a page that
                shows categories is converted to a honest-ui <Form>. */}
              <Heading as='h3'>{languageFile.uploadCategory}</Heading>
              <ul className={Styles.documentList}>{renderCategoryColumns()}</ul>
              <div data-testid={'Category'}>
                <HonestSelectCategory onUpload={onUpload} eventNum={''} uploadedFiles={documents} documentCategory={documentCategory} uploading={uploading} categoryList={categoryList} setDocumentCategory={setDocumentCategory}/>
              </div>
            </GridItem>) : (<>
              <GridItem colSpan={4} colSpanMd={6} colSpanLg={12}>
                <HonestUploadButton ref={uploadButtonRef} onUpload={onUpload} eventNum={''}/>
              </GridItem>
              <GridItem colSpan={4} colSpanMd={6} colSpanLg={12}>
                <ul className={Styles.documentList}>{documents.map(getSelectedDocument)}</ul>
              </GridItem>
            </>)}
        </Grid>
      </Loader>
    </div>);
};
export default HonestInlineDocumentUpload;
